import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, ArrowUpDown, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';

const selectClass =
  'h-8 border border-border bg-background px-2.5 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20';

// Fallback while the registry loads; the live list comes from tradingCariType.
const FALLBACK_CARI_TYPES = ['M', 'T', 'S', 'B', 'ORT', 'P', 'AH', 'E', 'X', 'I'];
const CURRENCY_COLS = ['EUR', 'GBP', 'USD', 'TRY'] as const;

export function CariBalancesPage() {
  const { t } = useTranslation('trading');
  const navigate = useNavigate();

  const [debounced, setDebounced] = useSessionState('trading:cari-balances:debounced', '');
  const [search, setSearch] = useState(debounced);
  const [cariType, setCariType] = useSessionState('trading:cari-balances:cariType', '');
  const [currency, setCurrency] = useSessionState('trading:cari-balances:currency', '');
  const [sortBy, setSortBy] = useSessionState<'name' | 'balance'>(
    'trading:cari-balances:sortBy',
    'name'
  );
  const [sortDir, setSortDir] = useSessionState<'asc' | 'desc'>(
    'trading:cari-balances:sortDir',
    'asc'
  );
  const [exporting, setExporting] = useState(false);

  const utils = trpc.useUtils();

  const { data: cariTypeRows } = trpc.tradingCariType.list.useQuery();
  const cariTypeCodes = cariTypeRows?.map((tp) => tp.code) ?? FALLBACK_CARI_TYPES;

  const onSearch = (v: string) => {
    setSearch(v);
    setTimeout(() => setDebounced(v), 300);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await utils.tradingCari.exportBalances.fetch({
        search: debounced || undefined,
        cariType: cariType || undefined,
        currency: (currency || undefined) as (typeof CURRENCY_COLS)[number] | undefined,
        sortBy,
        sortDir,
      });
      downloadXlsx(res.rows, 'Cari Bakiyeleri', `cari-bakiyeleri-${dateStamp()}.xlsx`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading } = trpc.tradingCari.balances.useQuery({
    search: debounced || undefined,
    cariType: cariType || undefined,
    currency: (currency || undefined) as (typeof CURRENCY_COLS)[number] | undefined,
    sortBy,
    sortDir,
  });

  const fmt = (n: number) =>
    new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      n
    );

  const typeLabel = (ct: string) =>
    t(
      `cari.${
        (
          {
            M: 'customer',
            T: 'supplier',
            S: 'vendor',
            B: 'dealer',
            ORT: 'partner',
            P: 'personnel',
            AH: 'openAccount',
            E: 'government',
            X: 'legal',
            I: 'insurance',
          } as Record<string, string>
        )[ct] ?? 'customer'
      }`
    );

  const cellFor = (row: NonNullable<typeof data>['items'][number], cur: string) => {
    const b = row.byCurrency[cur];
    if (!b) return null;
    return cur === 'TRY' ? b.tryNet : b.fxNet;
  };

  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('cariBalances.title', 'Cari Balances')}</h1>
          <p className="text-muted-foreground">
            {t(
              'cariBalances.subtitle',
              'Net balance per currency for every account with movements'
            )}
            {data ? ` (${data.total})` : ''}
          </p>
        </div>
        <Button variant="outline" onClick={onExport} disabled={exporting || !items.length}>
          {exporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          {t('cariBalances.export', 'Export')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="relative">
          <Label className="text-xs">{t('cariBalances.search', 'Search')}</Label>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t('cariBalances.searchPlaceholder', 'Name or cari code...')}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 mt-1 h-8 w-64 border px-3 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cariBalances.type', 'Type')}</Label>
          <select
            value={cariType}
            onChange={(e) => setCariType(e.target.value)}
            className={`${selectClass} block w-40`}
          >
            <option value="">{t('cariBalances.allTypes', 'All types')}</option>
            {cariTypeCodes.map((ct) => (
              <option key={ct} value={ct}>
                {ct} · {typeLabel(ct)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cariBalances.currency', 'Currency')}</Label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className={`${selectClass} block w-28`}
          >
            <option value="">{t('cariBalances.allCurrencies', 'All')}</option>
            {CURRENCY_COLS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (sortBy === 'balance') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
            else {
              setSortBy('balance');
              setSortDir('desc');
            }
          }}
        >
          <ArrowUpDown className="mr-2 h-3.5 w-3.5" />
          {sortBy === 'balance'
            ? t('cariBalances.sortByBalance', 'Balance') + (sortDir === 'asc' ? ' ↑' : ' ↓')
            : t('cariBalances.sortByName', 'Sort by balance')}
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="px-3 py-3 text-left font-medium">{t('cariBalances.cari', 'Cari')}</th>
              <th className="px-3 py-3 text-left font-medium">{t('cariBalances.type', 'Type')}</th>
              {CURRENCY_COLS.map((c) => (
                <th key={c} className="px-3 py-3 text-right font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={2 + CURRENCY_COLS.length} className="py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td
                  colSpan={2 + CURRENCY_COLS.length}
                  className="text-muted-foreground py-8 text-center"
                >
                  {t('cariBalances.noResults', 'No accounts found')}
                </td>
              </tr>
            )}
            {items.map((row) => (
              <tr
                key={`${row.source}-${row.id}`}
                className="hover:bg-muted/30 cursor-pointer border-b last:border-0"
                onClick={() => navigate(`/trading/cari/${row.source}/${row.id}/movements`)}
              >
                <td className="px-3 py-2.5">
                  <span className="font-medium">{row.companyName}</span>
                  {row.legacyCariCode && (
                    <span className="text-muted-foreground ml-2 font-mono text-xs">
                      {row.legacyCariCode}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <span className="bg-muted rounded px-1.5 py-0.5 text-xs">{row.cariType}</span>
                </td>
                {CURRENCY_COLS.map((c) => {
                  const v = cellFor(row, c);
                  return (
                    <td
                      key={c}
                      className={`px-3 py-2.5 text-right tabular-nums ${v == null ? 'text-muted-foreground/40' : v >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {v == null ? '—' : fmt(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground text-xs">
        {t(
          'cariBalances.legend',
          'Green = they owe us (debit) · Red = we owe them (credit). Click a row to open the ledger.'
        )}
      </p>
    </div>
  );
}
